import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { ICompanyProviders } from 'store/companies/companiesTypes';
import { RootState } from 'store/store';
import {
  requireDocument,
  requireDocumentFn,
} from 'store/providers/providerSlice';
import TaskFileExplorerModal from '../TaskFileExplorerModal';
import {
  TaskFileBrowseIcon,
  TaskFilePickerWrapper,
  TaskRelatedItems,
} from './styles';
import { TASK_CHIP_TRANSITION_CLASS_NAME } from './TaskFileChip/styles';
import Icon from 'components/Icon';
import TaskFileChip from './TaskFileChip';
import notify from '../../../../components/Notify';
import { appDispatch } from 'store/store';
import {
  IProviderItemViewWithData,
  IRelatedProviderItem,
  ProviderItemType,
} from 'store/providers/providerTypes';
import { ITask } from 'store/tasks/tasksType';
import i18n from '../../../../i18n';
import { useTranslation } from 'react-i18next';
import { OpenItemsContext } from '../../../../store/context/OpenItemsContext';
import { SURVEY_ANALYSE_LINK_DESC_NAME } from '../../../../store/survey/surveyTypes';
import { black, exactWhite } from 'utils/style/colors';

interface IOwnProps {
  defaultItem?: IRelatedProviderItem;
  items: IRelatedProviderItem[] | null;
  onRemove: (
    item: IRelatedProviderItem
  ) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onSave: (item: IRelatedProviderItem) => void;
  providers: ICompanyProviders | null;
  title?: string;
  task?: ITask;
  editable?: boolean;
  isRequired?: boolean;
}

interface IStateToProps {
  providerItems?: any;
}

interface IDispatchToProps {
  requireProviderItem: (
    providerItemId: number
  ) => ReturnType<typeof requireDocumentFn>;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

// const onOpenDocument = (item: IProviderItemViewWithData) => {
//   window.open(item.data.document.url, '_blank');
// };

const onOpenFolder = (_item: IProviderItemViewWithData) => {
  window.open(`/content/${_item.data.providerId}/f/${_item.data.id}`, '_blank');
};

const onOpenLink = (item: IProviderItemViewWithData) => {
  window.open(`/content/${item.data!.linkedProviderId}`, '_blank');
};

const getOpenFunction: {
  [key in Extract<ProviderItemType, 'link' | 'folder'>]: (
    item: IProviderItemViewWithData
  ) => void;
} = {
  // document: onOpenDocument,
  folder: onOpenFolder,
  link: onOpenLink,
};

const TaskFilePicker: React.FC<IComponentProps> = ({
  defaultItem,
  items,
  onRemove,
  onSave,
  providerItems,
  providers,
  requireProviderItem,
  title,
  editable,
  task,
  isRequired,
}) => {
  const cntx = useContext(OpenItemsContext);

  const [showFileExplorerModal, setShowFileExplorerModal] = useState(false);

  const [loadingItemId, setLoadingItemId] = useState<number | undefined>(
    undefined
  );
  const [selectedItem, setSelectedItem] = useState<
    IRelatedProviderItem | undefined
  >(defaultItem);

  const { t } = useTranslation('taskManager');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      !params.get('id') &&
      selectedItem &&
      cntx.openItems.includes(selectedItem?.id)
    ) {
      cntx.changeOpenItems;
    }
  }, [window.location.search]);

  const handleShowFileExplorerModal = () => {
    setShowFileExplorerModal((prevState) => !prevState);
  };

  const handleShowFileExplorerModalCallback = useCallback(
    handleShowFileExplorerModal,
    []
  );

  const getProviderItem = (item?: IRelatedProviderItem) => {
    let existItem: IProviderItemViewWithData | undefined;
    if (providerItems && item) {
      const foundItem = providerItems[item.providerId].items[item.id];
      if (foundItem) {
        existItem = foundItem;
      }
    }
    return existItem;
  };

  const getProviderItemCallback = useCallback(getProviderItem, [
    providerItems,
    selectedItem,
  ]);

  const handleOpenItem =
    (item?: IRelatedProviderItem) =>
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.preventDefault();
      setSelectedItem(item);
      if (item) {
        const { id, type } = item;

        const openFunction = getOpenFunction[type] || onOpenItem;

        const itemInStore = getProviderItemCallback(item);

        if (itemInStore) {
          openFunction(itemInStore);
        } else {
          setLoadingItemId(id);
          requireProviderItem(id).then(
            (res) => {
              setLoadingItemId(undefined);
              openFunction({ data: res.item });
            },
            () => {
              setLoadingItemId(undefined);
              notify(t('couldNotLoadItem'), 'error');
            }
          );
        }
      }
    };
  const handleOpenItemCallback = useCallback(handleOpenItem, [
    providerItems,
    task,
  ]);

  const onOpenItem = (item: IProviderItemViewWithData) => {
    const otherProps = {} as any;
    if (item.data.type === 'survey') {
      // @ts-expect-error false error!
      const responseId = task?.fields
        ?.find((it) => it.type === 'responseLink')
        ?.value.split('@')[0];
      // @ts-expect-error false error!
      const analyseId = task?.fields
        .find((it) => it.type === 'analyseLink')
        ?.value.split('@')[0];
      otherProps.filteredResponseId = responseId && Number(responseId);
      otherProps.filteredAnalyseId = analyseId && Number(analyseId);
      if (otherProps.filteredAnalyseId) {
        const sourceItemId = item.data.referenced.find(
          (it) => it.description === SURVEY_ANALYSE_LINK_DESC_NAME
        )?.sourceItemId;
        if (sourceItemId) {
          const itemInStore = getProviderItemCallback({
            providerId: item.data.providerId,
            id: sourceItemId,
            title: '',
            type: 'survey',
          });
          if (itemInStore) {
            cntx.handleOpenItem(
              itemInStore,
              'analyse',
              undefined,
              undefined,
              otherProps
            );
          } else {
            setLoadingItemId(item.data.id);
            requireProviderItem(sourceItemId)
              .then((res) => {
                setLoadingItemId(undefined);
                cntx.handleOpenItem(
                  res.item,
                  'analyse',
                  undefined,
                  undefined,
                  otherProps
                );
              })
              .catch(() => {
                setLoadingItemId(undefined);
                notify(t('couldNotLoadItem'));
              });
          }
        }
        return;
      }
    }
    cntx.handleOpenItem(item, 'responses', undefined, undefined, otherProps);
  };

  const handleSaveItem = (item: IRelatedProviderItem) => {
    onSave(item);
    // to store last opened item position
    setSelectedItem(item);
    setShowFileExplorerModal(false);
  };
  const handleSaveItemCallback = useCallback(handleSaveItem, []);
  const finalTitle = isRequired
    ? '* ' + title || i18n.t('relatedItems', { ns: 'taskManager' })
    : title || i18n.t('relatedItems', { ns: 'taskManager' });

  if (!cntx) return <></>;
  return (
    <TaskFilePickerWrapper>
      <div>
        <div
          style={{
            display: 'inline-block',
            fontWeight: 'bold',
            fontSize: '1rem',
            margin: '0.5rem 0',
          }}
        >
          <div style={{ color: black }}>
            {finalTitle
              ? finalTitle
              : (i18n.t('relatedItems', { ns: 'taskManager' }) as string)}
          </div>
          {editable && (
            <TaskFileBrowseIcon onClick={handleShowFileExplorerModalCallback}>
              <Icon type="plus" color={exactWhite} />
            </TaskFileBrowseIcon>
          )}
        </div>
      </div>
      <TaskRelatedItems>
        <TransitionGroup>
          {items &&
            items.map((item) => {
              return (
                <CSSTransition
                  key={item.id}
                  timeout={300}
                  classNames={TASK_CHIP_TRANSITION_CLASS_NAME}
                >
                  <TaskFileChip
                    item={item}
                    key={item.id}
                    onOpen={handleOpenItemCallback}
                    isLoading={
                      loadingItemId ? loadingItemId === item.id : false
                    }
                    onRemove={editable ? onRemove : undefined}
                  />
                </CSSTransition>
              );
            })}
        </TransitionGroup>
      </TaskRelatedItems>
      {providers && showFileExplorerModal && (
        <TaskFileExplorerModal
          onClose={handleShowFileExplorerModalCallback}
          onSave={handleSaveItemCallback}
          providers={providers}
          defaultItem={selectedItem}
        />
      )}
      {/*{openItem && (*/}
      {/*  <DataContextProvider value={{ providerId: openItem.data.providerId }}>*/}
      {/*    <TaskItemsViewer*/}
      {/*      dispatch={appDispatch}*/}
      {/*      onClose={setOpenItem}*/}
      {/*      openItem={openItem}*/}
      {/*      openItemProps={openItemProps}*/}
      {/*    />*/}
      {/*  </DataContextProvider>*/}
      {/*)}*/}
    </TaskFilePickerWrapper>
  );
};

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const providerItems = state.providers.items;

  return { providerItems };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    requireProviderItem: (providerItemId: number) =>
      appDispatch(requireDocument({ documentId: providerItemId })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskFilePicker);
